import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeading from '@/components/feature/SectionHeading';
import EmptyState from '@/components/feature/EmptyState';
import { specials } from '@/mocks/specials';
import { movies } from '@/mocks/movies';

export default function SpecialDetail() {
  const { id } = useParams();
  const special = specials.find((s) => s.id === id);

  if (!special) {
    return (
      <div className="w-full bg-background-50 min-h-[60vh] flex items-center justify-center px-4">
        <EmptyState
          icon="ri-gallery-line"
          title="专题不存在"
          description="你访问的专题可能已下线或链接有误。"
          actionLabel="返回专题合集"
          onAction={() => window.REACT_APP_NAVIGATE?.('/specials')}
        />
      </div>
    );
  }

  const specialMovies = special.movieIds
    .map((mid) => movies.find((m) => m.id === mid))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  const otherSpecials = specials.filter((s) => s.id !== special.id).slice(0, 3);

  return (
    <div className="w-full bg-background-50">
      <section className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
        <img
          src={special.cover}
          alt={`${special.title} 专题主图`}
          title={`${special.title} - 电影专题合集`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/75 to-background-50/30"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-8 pb-8">
            <div className="max-w-screen mx-auto">
              <nav className="text-xs md:text-sm text-foreground-600 mb-4 flex items-center gap-1.5 flex-wrap">
                <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/specials" className="hover:text-primary-400 transition-colors">专题合集</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-800 truncate max-w-[200px]">{special.title}</span>
              </nav>
              <h1 className="text-2xl md:text-4xl font-black text-foreground-950 mb-2">{special.title}</h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">{special.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-10">
        <div className="max-w-screen mx-auto">
          <p className="text-sm md:text-base text-foreground-700 leading-loose max-w-3xl mb-10">{special.description}</p>

          <SectionHeading title="专题收录影片" subtitle={`共 ${specialMovies.length} 部`} />
          {specialMovies.length === 0 ? (
            <div className="bg-background-100 border border-background-300/50 rounded-lg">
              <EmptyState icon="ri-film-line" title="专题内容整理中" description="该专题的影片正在补充，敬请期待。" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {specialMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}

          <section className="mt-14">
            <SectionHeading title="更多专题推荐" moreTo="/specials" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {otherSpecials.map((s) => (
                <Link
                  key={s.id}
                  to={`/specials/${s.id}`}
                  className="group bg-background-100 border border-background-300/50 rounded-lg overflow-hidden hover:border-primary-500/60 transition-colors"
                >
                  <div className="w-full aspect-[16/9] overflow-hidden bg-background-200">
                    <img
                      src={s.cover}
                      alt={`${s.title} 专题主图`}
                      title={`${s.title} - 电影专题`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-400 transition-colors">{s.title}</h3>
                    <p className="text-xs text-foreground-600 mt-1 line-clamp-2">{s.subtitle}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}